import { UiState } from '../state/UiState';
import { t } from '../i18n/i18n';

export interface AudioSettingsPanelCallbacks {
  onUpgradeRequested: (feature: string) => void;
}

export class AudioSettingsPanel {
  private container: HTMLElement;
  private uiState: UiState;
  private callbacks: AudioSettingsPanelCallbacks;

  private audioCtx: AudioContext | null = null;
  private lockOverlayEl!: HTMLElement;
  private lockBadgeEl!: HTMLElement;
  private lockDescEl!: HTMLElement;
  private btnUpgradeEl!: HTMLButtonElement;
  private seSelectTitleEl!: HTMLElement;
  private sePurinBtn!: HTMLButtonElement;
  private sePoyonBtn!: HTMLButtonElement;
  private seWaterBtn!: HTMLButtonElement;
  private volumeTitleEl!: HTMLElement;
  private volumeSlider!: HTMLInputElement;
  private volumeVal!: HTMLElement;

  constructor(container: HTMLElement, uiState: UiState, callbacks: AudioSettingsPanelCallbacks) {
    this.container = container;
    this.uiState = uiState;
    this.callbacks = callbacks;

    this.render();
    this.initElements();
    this.bindEvents();

    this.uiState.subscribe(() => {
      this.updateView();
      this.updateTexts();
    });
  }

  private render(): void {
    const texts = t();

    this.container.innerHTML = `
      <div class="pro-lock-overlay" id="lockAudio">
        <div class="pro-lock-badge" id="lockAudioBadge">🔒 ${texts.upgrade.title}</div>
        <p id="lockAudioDesc" style="font-size: 12px; color: var(--color-text-muted);">${texts.upgrade.perkAudio}</p>
        <button class="btn btn-primary" id="btnUpgradeAudio" style="padding: 6px 14px; font-size: 12px;">${texts.upgrade.ok}</button>
      </div>

      <div class="control-group">
        <div class="control-header">
          <span id="seSelectTitle">${texts.audio.seSelect}</span>
        </div>
        <div style="display: flex; gap: 6px;">
          <button class="btn btn-secondary btn-se" id="btnSePurin" data-se="purin">${texts.audio.sePurin}</button>
          <button class="btn btn-secondary btn-se" id="btnSePoyon" data-se="poyon">${texts.audio.sePoyon}</button>
          <button class="btn btn-secondary btn-se" id="btnSeWater" data-se="water">${texts.audio.seWater}</button>
        </div>
      </div>

      <div class="control-group">
        <div class="control-header">
          <span id="volumeTitle">${texts.audio.masterVolume}</span>
          <span class="control-value" id="valAudioVolume">80%</span>
        </div>
        <input type="range" id="sliderAudioVolume" min="0" max="100" value="80">
      </div>
    `;
  }

  private initElements(): void {
    this.lockOverlayEl = this.container.querySelector('#lockAudio') as HTMLElement;
    this.lockBadgeEl = this.container.querySelector('#lockAudioBadge') as HTMLElement;
    this.lockDescEl = this.container.querySelector('#lockAudioDesc') as HTMLElement;
    this.btnUpgradeEl = this.container.querySelector('#btnUpgradeAudio') as HTMLButtonElement;
    this.seSelectTitleEl = this.container.querySelector('#seSelectTitle') as HTMLElement;
    this.sePurinBtn = this.container.querySelector('#btnSePurin') as HTMLButtonElement;
    this.sePoyonBtn = this.container.querySelector('#btnSePoyon') as HTMLButtonElement;
    this.seWaterBtn = this.container.querySelector('#btnSeWater') as HTMLButtonElement;
    this.volumeTitleEl = this.container.querySelector('#volumeTitle') as HTMLElement;
    this.volumeSlider = this.container.querySelector('#sliderAudioVolume') as HTMLInputElement;
    this.volumeVal = this.container.querySelector('#valAudioVolume') as HTMLElement;
  }

  private updateTexts(): void {
    const texts = t();
    if (this.lockBadgeEl) this.lockBadgeEl.textContent = `🔒 ${texts.upgrade.title}`;
    if (this.lockDescEl) this.lockDescEl.textContent = texts.upgrade.perkAudio;
    if (this.btnUpgradeEl) this.btnUpgradeEl.textContent = texts.upgrade.ok;
    if (this.seSelectTitleEl) this.seSelectTitleEl.textContent = texts.audio.seSelect;
    if (this.sePurinBtn) this.sePurinBtn.textContent = texts.audio.sePurin;
    if (this.sePoyonBtn) this.sePoyonBtn.textContent = texts.audio.sePoyon;
    if (this.seWaterBtn) this.seWaterBtn.textContent = texts.audio.seWater;
    if (this.volumeTitleEl) this.volumeTitleEl.textContent = texts.audio.masterVolume;
  }

  private bindEvents(): void {
    this.container.querySelectorAll<HTMLButtonElement>('.btn-se').forEach((btn) => {
      btn.addEventListener('click', () => {
        const se = btn.getAttribute('data-se') as 'purin' | 'poyon' | 'water';
        if (se) {
          this.uiState.setAudio(se);
          this.playSound(se);
        }
      });
    });

    this.volumeSlider.addEventListener('input', () => {
      const vol = parseInt(this.volumeSlider.value, 10);
      this.volumeVal.textContent = vol + '%';
      this.uiState.setAudio(undefined, vol);
    });

    this.btnUpgradeEl?.addEventListener('click', () => {
      const texts = t();
      this.callbacks.onUpgradeRequested(texts.upgrade.perkAudio);
    });
  }

  private updateView(): void {
    const isAllowed = this.uiState.isAudioAllowed();
    this.lockOverlayEl.style.display = isAllowed ? 'none' : 'flex';
  }

  // --- Web Audio API 合成音再生 ---
  playSound(type: 'purin' | 'poyon' | 'water'): void {
    if (!this.uiState.isAudioAllowed()) return;
    const state = this.uiState.getSnapshot();
    if (state.audio.isMuted) return;

    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!this.audioCtx) {
        this.audioCtx = new AudioCtx();
      }
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      const now = this.audioCtx.currentTime;
      const osc = this.audioCtx.createOscillator();
      const gain = this.audioCtx.createGain();
      const vol = (state.audio.masterVolume / 100) * 0.25;

      osc.connect(gain);
      gain.connect(this.audioCtx.destination);

      if (type === 'purin') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(340, now);
        osc.frequency.exponentialRampToValueAtTime(580, now + 0.08);
        osc.frequency.exponentialRampToValueAtTime(420, now + 0.22);
        gain.gain.setValueAtTime(vol, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
        osc.start(now);
        osc.stop(now + 0.25);
      } else if (type === 'poyon') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(480, now);
        osc.frequency.exponentialRampToValueAtTime(220, now + 0.3);
        gain.gain.setValueAtTime(vol, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
        osc.start(now);
        osc.stop(now + 0.3);
      } else {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.exponentialRampToValueAtTime(1200, now + 0.05);
        gain.gain.setValueAtTime(vol * 0.7, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
        osc.start(now);
        osc.stop(now + 0.15);
      }
    } catch {
      // AudioContext unavailable in silent environments
    }
  }
}
